'use client';

import { useState, useRef, useEffect, ReactNode } from 'react';
import { A11y, Keyboard, Navigation } from 'swiper';
import { Swiper, type SwiperProps, type SwiperRef, SwiperSlide } from 'swiper/react';
import { Card, CardProps as CardProps } from '../Card/index';
import { Popup } from '../Popup';
import { TextSectionBlock, TextSectionBlockProps } from '../TextSectionBlock';
import { cn, tv } from '../_lib/utils';
import { Container } from '../Container';
import { breakpoints } from '../_lib/breakpoints.mjs';
import { useMediaQuery } from '../_lib/useMediaQuery';

const carouselStyles = tv({
	slots: {
		content:
			'relative flex flex-col max-md:bg-background-primary md:justify-center md:items-end w-full md:flex-[0_0_30%] z-10 after:absolute before:absolute md:before:bg-background-primary md:before:top-0 md:before:bottom-0 md:before:right-0 md:before:w-[50vw] md:before:z-[-1]',
		desktopGradient:
			'md:m-0 md:mr-[5rem] lg:mr-[7.5rem] md:after:-right-[5rem] lg:after:-right-[7.5rem] md:after:top-0 md:after:w-[5rem] lg:after:w-[7.5rem] md:after:h-full md:after:bg-linear-to-r md:after:from-background-primary md:after:to-background-primary/0',
		mobileGradientBottom:
			'after:w-[calc(100%+(var(--container-padding)*2))] after:-right-(--container-padding) after:-bottom-10 after:h-10 after:bg-linear-to-b after:from-background-primary after:to-background-primary/0',
	},
});

type CardCarouselItemProps = CardProps & {
	popupContent?: ReactNode;
};

export type CardCarouselProps = {
	className?: string;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	items?: CardCarouselItemProps[];
};

export function CardCarousel(props: CardCarouselProps) {
	const isSmallLayout = useMediaQuery(breakpoints.minMedium.query);
	const [activeIndex, setActiveIndex] = useState(0);
	const [swiper, updateSwiper] = useState<SwiperRef['swiper']>();
	const { content, desktopGradient, mobileGradientBottom } = carouselStyles();

	const cardSizeProps: Partial<CardProps> = {
		layout: isSmallLayout ? 'horizontal' : 'vertical',
		size: isSmallLayout ? 'xl' : 'sm',
	};

	const SwiperConfig: SwiperProps = {
		centeredSlides: false,
		grabCursor: true,
		keyboard: {
			enabled: true,
		},
		slidesPerView: 1,
		spaceBetween: 32,
		speed: 480,
		breakpoints: {
			1024: {
				slidesPerView: 1.2,
			},
			1536: {
				slidesPerView: 1.5,
			},
			2048: {
				slidesPerView: 1.8,
			},
		},
		onSlideChange(swiper) {
			setActiveIndex(swiper.activeIndex);
		},
		onSwiper(swiper) {
			updateSwiper(swiper);
		},
	};

	const divRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (divRef.current) {
			const isTouchScreen = 'ontouchstart' in window;
			if (isTouchScreen) {
				divRef.current.setAttribute('data-lenis-prevent', 'true');
			} else {
				divRef.current.removeAttribute('data-lenis-prevent');
			}
		}
	}, []);

	return (
		<div className={cn('overflow-hidden', props.className)} data-component='CardCarousel'>
			<Container className='relative flex flex-col gap-responsive-4xl'>
				<div className='flex flex-col md:flex-row'>
					<div className={`${content()} ${desktopGradient()} ${mobileGradientBottom()}`}>
						{props.textSectionBlock && <TextSectionBlock className='py-spacing-lg md:py-0' size='md' {...props.textSectionBlock} />}
					</div>

					<div ref={divRef} className={`relative pt-4 md:pt-0 md:self-center min-w-0 w-full h-full z-0`}>
						<Swiper
							modules={[Navigation, A11y, Keyboard]}
							{...SwiperConfig}
							style={{ overflow: 'visible' }}
							className='[&>div]:flex [&>div]:flex-row [&_.swiper-slide]:h-auto [&_div[data-component="Media"]]:@container-normal!'
						>
							{props.items?.map((item, index) => {
								return (
									item && (
										<SwiperSlide key={`card-carousel-${index}`}>
											{item.popupContent ? (
												<Popup layout='sidebar' triggerButton={<Card {...item} {...cardSizeProps} />}>
													{item.popupContent}
												</Popup>
											) : (
												<Card {...item} {...cardSizeProps} />
											)}
										</SwiperSlide>
									)
								);
							})}
						</Swiper>
					</div>
				</div>

				<div className='flex flex-row justify-center'>
					{props.items?.map((_, index) => {
						return (
							<PaginationItem
								key={`card-carousel-pagination-${index}`}
								active={index === activeIndex}
								onClick={() => swiper?.slideTo(index, 400)}
							/>
						);
					})}
				</div>
			</Container>
		</div>
	);
}

const styles = tv({
	slots: {
		item: 'transition-all duration-fast ease-smooth before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute before:content[""]',
	},
	variants: {
		active: {
			true: {
				item: 'opacity-100 w-[3rem]',
			},
			false: {
				item: 'opacity-30 w-[1.5rem] group-hover:cursor-pointer group-hover:opacity-60 group-hover:w-[2rem]',
			},
		},
	},
});

export function PaginationItem({ active, onClick }: { active: boolean; onClick?(): void }) {
	const { item } = styles({ active });
	return (
		<div className='group relative px-1 py-4' onClick={onClick}>
			<div className={`h-1 rounded-full bg-background-brand ${item()}`} />
		</div>
	);
}
